'use client';

import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '@/components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './slider.scss';
import { Badge } from '@/components';
import { ExternalLinkWarning } from '@/components/ExternalLinkWarning';
import { DappModal } from '@/components/DappModal';
import { EcosystemDapp } from '@/hooks/content';

const DEFAULT_CAROUSEL_SETTINGS = {
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  initialSlide: 0,
  centerMode: false,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: false,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: 1,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};
const CustomArrow = ({
  direction,
  visibleSlides,
  ...props
}: {
  direction: 'left' | 'right';
  className?: string;
  slideCount?: number;
  visibleSlides?: number;
  slidesToShow?: number;
  onClick?: () => void;
}) => {
  const { onClick, className, slideCount } = props;

  if (visibleSlides && slideCount && visibleSlides >= slideCount) return null;
  const isArrowDisabled = className?.includes('slick-disabled');

  if (slideCount)
    return (
      <div
        onClick={onClick}
        className={`absolute ${direction === 'left' ? 'left-[-30px]' : 'right-[-30px]'} top-[50%] translate-y-[-50%] z-10 cursor-pointer border rounded-full border-[#404040] bg-neutral-950 flex justify-center items-center w-[40px] h-[40px]`}
      >
        <Image
          src={`/assets/icons/${isArrowDisabled ? 'slider-arrow-left.svg' : 'slider-arrow-left-active.svg'}`}
          width={20}
          height={20}
          alt="Icn"
          className={`h-auto hover:fill-grey-500 ${direction === 'right' ? 'rotate-180' : ''}`}
        />
      </div>
    );
};
export const FeaturedProjectSection = ({
  title,
  projects,
  isLoading,
  showExpandButton = true,
}: {
  title?: string;
  projects: EcosystemDapp[];
  isLoading?: boolean;
  showExpandButton?: boolean;
}) => {
  const sliderRef = useRef<Slider | null>(null);
  const [visibleSlides, setVisibleSlides] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [currentDapp, setCurrentDapp] = useState<EcosystemDapp | undefined>(undefined);

  const featuredDappsLen = projects?.length;

  const settings = {
    ...DEFAULT_CAROUSEL_SETTINGS,
    nextArrow: <CustomArrow direction="right" visibleSlides={visibleSlides} />,
    prevArrow: <CustomArrow direction="left" visibleSlides={visibleSlides} />,
    onReInit: () => {
      getActualSlidesToShow();
    },
    onSetPosition: () => {
      getActualSlidesToShow();
    },
  };

  // Get actual amount of shown slides to hide arrows or not
  const getActualSlidesToShow = useCallback(() => {
    if (sliderRef.current) {
      const slick = sliderRef?.current;

      const actualSlidesToShow = slick?.props?.slidesToShow ?? featuredDappsLen;

      setVisibleSlides(actualSlidesToShow); // Update state
    }
  }, [featuredDappsLen]);

  useEffect(() => {
    getActualSlidesToShow();
  }, [getActualSlidesToShow]);

  const handleOpen = (dapp: EcosystemDapp) => {
    setIsOpen(true);
    setCurrentDapp(dapp);
  };

  const handleClose = () => {
    setIsOpen(false);
    setCurrentDapp(undefined);
  };

  if (isLoading) return <Spinner />;

  if (!projects.length) return null;

  return (
    <div id="featured-projects" className="container mx-auto">
      <div
        className={`font-chakra my-0 mb-4 mx-auto flex w-max rounded-lg border border-neutral-900 relative text-neutral-100 uppercase text-base font-normal px-4 bg-neutral-1000 z-10 py-1`}
      >
        {title}
      </div>
      <div className="px-6 xl:px-0 lg:px-6 md:px-6 sm:px-6 mx-auto">
        <div className="rounded-2xl px-2">
          {/* @ts-expect-error something is wrong with the types */}
          <Slider ref={sliderRef} {...settings} className="px-4">
            {projects.map((dapp, ix) => {
              return (
                <div className="h-full" key={`${dapp.name}-${ix}`}>
                  <div className="h-full flex-1 flex justify-between flex-col border border-neutral-950 rounded-xl bg-neutral-1000 overflow-hidden">
                    <div>
                      <ExternalLinkWarning href={dapp.website || ''}>
                        <div
                          style={{
                            ['--image-url' as string]: `url(${dapp.background})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                          className="h-[94px] bg-[image:var(--image-url)] bg-no-repeat relative bg-cover"
                        >
                          {dapp.logo && (
                            <Image
                              src={dapp.logo}
                              width={44}
                              height={44}
                              alt="Icn"
                              className="h-auto absolute left-[50%] translate-x-[-50%] bottom-[-22px] border border-neutral-900 rounded"
                            />
                          )}
                        </div>
                      </ExternalLinkWarning>
                      <div className="flex flex-col gap-2 items-center mt-[42px] px-4 pb-5 text-center">
                        {dapp.name && (
                          <span
                            className={`font-chakra flex gap-2 items-center text-lg font-bold tracking-[0.4px] text-neutral-100 uppercase`}
                          >
                            {dapp.name}
                            {dapp.website && (
                              <ExternalLinkWarning href={dapp.website}>
                                <Image
                                  src="/assets/icons/chain-icon.svg"
                                  width={16}
                                  height={16}
                                  alt="Icn"
                                  className="h-auto"
                                />
                              </ExternalLinkWarning>
                            )}
                          </span>
                        )}
                        {!!dapp.connection?.length && (
                          <div className="flex gap-x-2">
                            {dapp.connection?.map((connection, index) => (
                              <Badge key={`${connection}-${index}`} className="text-neutral-500">
                                By {connection}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {dapp.description && (
                          <p className={`font-sans text-[#ADAEB1] text-xs font-normal mb-[20px]`}>{dapp.description}</p>
                        )}
                        <div className="flex gap-x-2">
                          {dapp.category && <Badge className="text-neutral-100">{dapp.category}</Badge>}
                          {!dapp.state && <Badge className="text-sky-600">Coming soon</Badge>}
                        </div>
                      </div>
                    </div>
                    {showExpandButton && (
                      <div className="flex justify-center items-center bg-neutral-1000 border-t border-grey-950 border-neutrl-950 py-4 px-4">
                        <Button
                          onClick={() => handleOpen(dapp)}
                          style={{ backgroundColor: 'rgba(223, 224, 225, 0.04)' }}
                          className={`font-sans text-[#DFE0E1] text-base flex justify-center items-center py-[7px] px-4 w-full gap-2 border border-neutral-900 rounded-lg normal-case`}
                        >
                          Expand
                          <Image src="/assets/icons/expand-icon.svg" width={16} height={16} alt="open modal window" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
      {currentDapp && <DappModal handleClose={handleClose} isOpen={isOpen} dapp={currentDapp} />}
    </div>
  );
};
